import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSnackBar } from '../../../components/FlowBuilder/Common/SnackBar/index.tsx';
import { templateService } from '../services/templateService';
import { normalizeWabaApp } from '../utils/normalizeTemplate';
import {
  extractApiErrorMessage,
  isEmptyWabaBusinessError,
  normalizeWabaAppsResponse,
} from '../utils/wabaAppUtils';

export default function useWabaApps() {
  const { showSnackBar } = useSnackBar();
  const [apps, setApps] = useState([]);
  const [search, setSearch] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState('');

  const fetchApps = useCallback(async () => {
    setIsLoading(true);
    setLoadError('');

    try {
      const res = await templateService.getWabaAccounts();
      const raw = normalizeWabaAppsResponse(res?.data ?? res);
      setApps(raw.map(normalizeWabaApp));
    } catch (error) {
      const message = extractApiErrorMessage(error);

      if (isEmptyWabaBusinessError(message)) {
        setApps([]);
        return;
      }

      setLoadError(message);
      setApps([]);
      showSnackBar(message, 'error');
    } finally {
      setIsLoading(false);
    }
  }, [showSnackBar]);

  useEffect(() => {
    fetchApps();
  }, [fetchApps]);

  const filteredApps = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return apps;

    return apps.filter(
      (app) =>
        app.app_name?.toLowerCase().includes(query) ||
        app.wa_phone_number?.includes(query) ||
        app.waba_id?.includes(query) ||
        app.company_name?.toLowerCase().includes(query)
    );
  }, [apps, search]);

  const liveCount = useMemo(() => apps.filter((app) => app.is_active).length, [apps]);
  const hasSearchQuery = Boolean(search.trim());
  const isEmpty = !isLoading && !loadError && apps.length === 0;
  const isSearchEmpty = !isLoading && !loadError && apps.length > 0 && filteredApps.length === 0;

  return {
    apps,
    filteredApps,
    search,
    setSearch,
    isLoading,
    loadError,
    liveCount,
    isEmpty,
    isSearchEmpty,
    hasSearchQuery,
    refresh: fetchApps,
  };
}
